/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  DayOfWeeks,
  DAYS,
  DayTimeOfWeeksType,
  DEFAULT_TIMEZONE,
  MoveTrigger,
  VerificationMode,
} from "../constant";
import { RemoteAEResponse } from "./ae";
import { OptionTypeBase } from "./common";
import { DateRange } from "./components/datetime";
import { Issuer } from "./issuer";
import { LocalAE } from "./localAE";
import { IPaginationBaseState } from "./components/pagination";
import {
  getCurrentDayInWeek,
  getCurrentTimeInDay,
  getDateInMonth,
  getMonthInYear,
} from "../utils/date";

export interface QueryGranularity {
  value?: string;
  option?: string;
  options?: OptionTypeBase[];
}

export interface ScheduleDayTimeModel {
  type?: DAYS | string;
  value?: DateRange[];
  options?: OptionTypeBase[];
  option?: string;
  queryGranularity?: QueryGranularity;
}

export interface DayTimeOfWeeksProps {
  data?: Array<ScheduleDayTimeModel>;
  onChange?: (model: Array<ScheduleDayTimeModel>) => void;
  type?: DayTimeOfWeeksType;
  readonly?: boolean;
  edit?: boolean;
}

export interface MigrationJobResponse {
  id?: string;
  name?: string;
  numberOfMigratedStudies?: number;
  numberOfWarnings: number;
  indexedStudies?: number;
  status?: string;
  paused?: boolean;
  processing?: boolean;
  autoStart?: boolean;
  errorCount?: number;
  errorCode?: string;
  errorMessage?: string;
  progress?: number;
  concurrentMoves?: number;
  studyCreateTimeout?: number;
  recurrence?: RecurrenceConfigurationRequest;
  enabled?: boolean;
  nextMigrationDate?: Date;
  sourceType?: string;
}

export interface MigrationJobStudyResponse {
  id?: string;
  patientId?: string;
  patientName?: string;
  patientSex?: string;
  patientDob?: string;
  studyInstanceUid?: string;
  studyDescription?: string;
  studyDate?: string;
  studyTime: string;
  accessionNumber: string;
  numberOfSeries: string;
  numberOfInstances: string;
  modalities: string[];
  migrationJob?: MigrationJobResponse;
  status?: string;
  accessionNumberIssuer?: Issuer;
}

export enum MigrationJobType {
  ONE_OFF = "ONE_OFF",
  REPEAT = "REPEAT",
}

export interface RecurrenceConfigurationRequest {
  number?: number;
  frequency?: string;
  onOrdinalDay?: string;
  onDays?: string[];
  onDate?: Date | string | number;
  onMonth?: number | string;
  atTime?: number;
  atTimezone?: string;
}

export interface MigrationJob {
  id?: string;
  name?: string;
  scheduleDayTime?: Array<ScheduleDayTimeModel>;
  studyDate?: DateRange;
  queryGranularity?: Array<ScheduleDayTimeModel>;
  automaticallyStart?: boolean;
  localApplicationEntity?: LocalAE | null;
  remoteApplicationEntity?: RemoteAEResponse | null;
  destinationApplicationEntities?: Array<LocalAE> | null;
  query?: any[];
  resultFilter?: any;
  exclusionRules?: any[]; // use state to render transform builder
  excludeFilePatterns?: string[]; // from response or request
  nextMoveTrigger?: string;
  verificationMode?: string;
  concurrentMoves: number;
  studyCreateTimeout?: number;
  jobType: MigrationJobType;
  recurrence?: RecurrenceConfigurationRequest;
  recurring?: boolean;
  paused?: boolean;
  // Recurrence model
  repeatStart?: Date | string;
  repeatEnd?: Date | string;
  studyMigrationDateFrom?: string;
  studyMigrationDateTo?: string;
  enabled?: boolean;
  status?: string;
  sourceType?: string; // migration DICOM from file system or not.
  path?: string;
}

export interface RangeTimeOfDays {
  startTime?: string;
  endTime?: string;
  period?: string | number;
}

export interface ListRangeTimeOfDay {
  dayOfWeek?: string;
  rangeTimeOfDays?: RangeTimeOfDays[];
}

export interface ScheduleDayTimeRequest {
  listRangeTimeOfDay?: ListRangeTimeOfDay[];
}

export interface MigrationJobRequest {
  id?: string;
  name?: string;
  studyDateStart?: Date | string;
  studyDateEnd?: Date | string;
  localApplicationEntity?: LocalAE | null;
  remoteApplicationEntity?: RemoteAEResponse | null;
  destinationApplicationEntities?: Array<LocalAE> | null;
  scheduleDayTime?: ScheduleDayTimeRequest;
  queryGranularity?: ScheduleDayTimeRequest;
  autoStart?: boolean;
  resultFilter?: any;
  query?: any[];
  nextMoveTrigger?: string;
  verificationMode?: string;
  concurrentMoves?: number;
  studyCreateTimeout?: number;
  jobType?: MigrationJobType;
  recurring?: boolean;
  recurrence?: RecurrenceConfigurationRequest;
  repeatStart?: Date | string;
  repeatEnd?: Date | string;
  studyMigrationDateFrom?: string;
  studyMigrationDateTo?: string;
  enabled?: boolean;
  status?: string;
  paused?: boolean;
  sourceType?: string;
  path?: string;
  excludeFilePatterns?: string[];
}

export interface MigrationJobsFormProps {
  model: MigrationJob;
  readOnly?: boolean;
  isUpdated?: boolean;
  onShowQueryBuilder?: (show?: boolean) => void;
  onSubmit?: (formValue: MigrationJob, formValid: boolean) => void;
}

export interface MigrationJobSearch extends IPaginationBaseState {
  searchKeyword?: string;
  page?: number;
  status?: string;
  localAes?: string[];
  remoteAes?: string[];
  destinationAes?: string[];
  recurring?: boolean;
  complete?: boolean;
  enabled?: boolean;
}

export const defaultValue: Readonly<MigrationJob> = {
  id: "",
  name: "",
  nextMoveTrigger: MoveTrigger.C_MOVE_COMPLETE,
  scheduleDayTime: [],
  queryGranularity: [],
  automaticallyStart: true,
  resultFilter: null,
  query: [],
  localApplicationEntity: null,
  remoteApplicationEntity: null,
  destinationApplicationEntities: null,
  verificationMode: VerificationMode.OBJECT_UID_MATCHING,
  concurrentMoves: 10,
  studyCreateTimeout: 15,
  jobType: MigrationJobType.ONE_OFF,
  recurring: false,
  paused: false,
  sourceType: "DICOM",
};

export enum MigrationJobTab {
  MIGRATION_JOBS = "MIGRATION_JOBS",
  QUERY = "QUERY",
  RESULT_FILTERS = "RESULT_FILTERS",
  RECURRENCE = "RECURRENCE",
  EXCLUSION_RULE = "EXCLUSION_RULE",
}

export enum RecurrenceRepeatType {
  DAY = "DAY",
  WEEK = "WEEK",
  MONTH = "MONTH",
  YEAR = "YEAR",
  HOUR = "HOUR",
}

export interface IRecurrenceRepeat {
  interval?: number;
  type?: string;
}

export interface IMonthFrequency {
  ordinal?: string;
  date?: number | string;
  day?: DayOfWeeks;
}

export interface IYearFrequency {
  ordinal?: string;
  day?: DayOfWeeks;
  date?: number | string;
  month?: number;
}

export interface IAtTimezoneSelect {
  time?: number;
  timeZone?: string;
}

export interface IMigrationDateRange {
  from?: string;
  to?: string;
}

export interface IRecurrence {
  repeat: IRecurrenceRepeat;
  starts?: Date | string | null;
  ends?: Date | string | null;
  weeksFrequency?: DayOfWeeks[];
  monthFrequency?: IMonthFrequency;
  yearFrequency?: IYearFrequency;
  at?: IAtTimezoneSelect;
  migrationDateRange?: IMigrationDateRange;
  enabled?: boolean;
}

export const defaultRecurrence: Readonly<IRecurrence> = {
  repeat: {
    interval: 1,
    type: RecurrenceRepeatType.DAY,
  },
  starts: new Date(),
  weeksFrequency: [getCurrentDayInWeek()],
  monthFrequency: {
    day: getCurrentDayInWeek(),
    date: getDateInMonth(),
    ordinal: undefined,
  },
  yearFrequency: {
    day: getCurrentDayInWeek(),
    date: getDateInMonth(),
    ordinal: undefined,
    month: getMonthInYear(),
  },
  at: {
    time: getCurrentTimeInDay(),
    timeZone: DEFAULT_TIMEZONE.value,
  },
  enabled: true,
};
